<template>
  <div class="admin-product-list">
    <h2>삭제 상품 목록보기</h2>
    <div class="button-box">
      <button @click="recoveryProduct" class="blue">삭제 상품 복구하기</button>
    </div>
    <div class="table-box">
      <h3>상품 목록 테이블</h3>
      <div ref="tableRef"></div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import "frappe-datatable/dist/frappe-datatable.min.css";
import DataTable from "frappe-datatable";
import {
  collection,
  query,
  orderBy,
  getDocs,
  where,
  doc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useBusyLock } from "@/lib/useBusyLock";

const { runLocked } = useBusyLock();

const tableRef = ref(null);
const dataTable = ref(null);
const originData = ref([]);

const recoveryProduct = () =>
  runLocked(async () => {
    const checkedItems = getCheckedItems();

    if (!checkedItems.length) {
      console.log("No items selected to sell on Cafe24.");
      alert("상품이 선택되지 않았습니다!");
      return;
    }

    if (!confirm("선택한 상품을 복구하시겠습니까?")) {
      return;
    }

    await Promise.all(
      checkedItems.map(async (item) => {
        try {
          await updateDoc(doc(db, "product", item.productId), {
            isActive: true,
            updatedAt: Timestamp.fromDate(new Date()),
          });
        } catch (error) {
          console.error(`Error updating item ${item.productId}:`, error);
        }
      })
    );

    alert("상품이 복구되었습니다!");
    window.location.reload();
  }).catch((error) => {
    console.error("Error selling selected items on Cafe24:", error);
  });

const getCheckedItems = () => {
  try {
    const checkedRows = dataTable.value?.rowmanager.getCheckedRows() || [];

    return checkedRows.map((rowIndex) => {
      const row = originData.value[Number(rowIndex)];
      console.log("Checked item:", row);
      return row;
    });
  } catch (error) {
    console.error("Error getting checked items:", error);
    return [];
  }
};

onMounted(() => {
  runLocked(async () => {
    // 1. Firebase에서 데이터 가져오기
    const q = query(
      collection(db, "product"),
      where("isActive", "==", false),
      orderBy("createdAt", "desc")
    );

    const querySnapshot = await getDocs(q);

    // 2. 문서들을 배열로 변환
    originData.value = querySnapshot.docs.map((docSnap) => ({
      ...docSnap.data(),
      id: docSnap.id,
    }));

    console.log(originData.value);

    const data = querySnapshot.docs.map((docSnap) => {
      const item = docSnap.data();

      return [
        {
          content: item.productThumbnailUrl?.originalUrl || "",
          editable: false,
        },
        {
          content: `<a href="${window.location.origin}/admin/product/add?id=${
            docSnap.id
          }">${item.productName ?? ""}</a>`,
          editable: false,
        },
        {
          content: item.viewMinUserGrade ?? "",
          editable: false,
        },
        {
          content: item.isRecommend ? "O" : "X",
          editable: false,
        },
        {
          content: item.productOriginPrice ?? 0,
          editable: false,
          format: (value) => `${Number(value || 0).toLocaleString()}원`,
        },
        {
          content: item.productBankSellPrice ?? 0,
          editable: false,
          format: (value) => `${Number(value || 0).toLocaleString()}원`,
        },
        {
          content: item.productSellPrice ?? 0,
          editable: false,
          format: (value) => `${Number(value || 0).toLocaleString()}원`,
        },
        {
          content: item.productBuyPrice ?? 0,
          editable: false,
          format: (value) => `${Number(value || 0).toLocaleString()}원`,
        },
        {
          content: item.productBuyDeliveryPrice ?? 0,
          editable: false,
          format: (value) => `${Number(value || 0).toLocaleString()}원`,
        },
        {
          content: item.optionList ?? [],
          editable: false,
          format: (value) => {
            const lines = (Array.isArray(value) ? value : []).map(
              (o) =>
                `${o.optionName} | ${Number(
                  o.optionStock || 0
                ).toLocaleString()}개`
            );
            return `<p>${lines.join("<br/>")}</p>`;
          },
        },
        item.createdAt?.toDate?.().toLocaleString?.() || "",
        item.updatedAt?.toDate?.().toLocaleString?.() || "",
      ];
    });

    // 3. frappe-datatable 생성
    dataTable.value = new DataTable(tableRef.value, {
      columns: [
        {
          name: "상품 이미지",
          editable: false,
          resizable: false,
          width: 128,
          align: "center",
          format: (value) => {
            return `<img src="${value}" style="width: 112px; height: 112px; object-fit: cover; object-position: center center;" />`;
          },
        },
        {
          name: "상품명",
          editable: false,
          resizable: false,
          width: 192,
          align: "center",
          format: (value) => `<p style="color: #007bff;">${value}</p>`,
        },
        {
          name: "최소 구매 등급",
          editable: false,
          resizable: false,
          width: 128,
          align: "center",
        },
        {
          name: "추천 여부",
          editable: false,
          resizable: false,
          width: 96,
          align: "center",
        },
        {
          name: "소비자가",
          editable: false,
          resizable: false,
          width: 96,
          align: "center",
        },
        {
          name: "계좌이체가",
          editable: false,
          resizable: false,
          width: 96,
          align: "center",
        },
        {
          name: "카드결제가",
          editable: false,
          resizable: false,
          width: 96,
          align: "center",
        },
        {
          name: "매입가",
          editable: false,
          resizable: false,
          width: 96,
          align: "center",
        },
        {
          name: "매입운송비",
          editable: false,
          resizable: false,
          width: 96,
          align: "center",
        },
        {
          name: "옵션",
          editable: false,
          resizable: false,
          width: 280,
          align: "center",
        },
        {
          name: "등록일",
          editable: false,
          resizable: false,
          width: 192,
          align: "center",
        },
        {
          name: "수정일",
          editable: false,
          resizable: false,
          width: 192,
          align: "center",
        },
      ],
      data,
      checkboxColumn: true,
      serialNoColumn: false,
      cellHeight: 128,
      inlineFilters: true,
    });
  }).catch((error) => {
    console.error("비활성 상품 목록 로딩 실패:", error);
    alert("비활성 상품 목록을 불러오지 못했습니다.");
  });
});
</script>

<style scoped lang="scss">
.admin-product-list {
  margin-top: 36px;

  > .button-box {
    margin-top: 24px;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;

    > button {
      padding: 8px 16px;
      border-radius: 4px;
      border: none;
      font-weight: 500;
      cursor: pointer;
      font-size: 14px;

      &.red {
        background-color: #dc3545;
        color: white;

        &:hover {
          background-color: #c82333;
        }
      }

      &.blue {
        background-color: #007bff;
        color: white;

        &:hover {
          background-color: #0056b3;
        }
      }

      &.secondary {
        background-color: #6c757d;
        color: white;

        &:hover {
          background-color: #545b62;
        }
      }
    }
  }

  > .table-box {
    margin-top: 24px;
    box-shadow: 8px 8px 16px rgba(0, 0, 0, 0.25);
    border-radius: 8px;
    padding: 24px;

    > div {
      margin-top: 16px;
      width: 100%;
      height: fit-content;
    }
  }
}
</style>
